<template>
  <div class="panel">
    <div class="panel-title">Recalcul de l'usage</div>
    <div class="panel-subtitle">
      Relance le calcul d'usage pour la période choisie, sur toutes les sociétés
      facturables.
    </div>

    <div class="panel-grid">
      <hit-select
        class="min-w-[20rem] w-full"
        :label="'Période'"
        :options="periodOptions"
        :value="selectedPeriod"
        item-id-property="id"
        :label-fields="['label']"
        clearable
        @change="onPeriodChange"
      />
    </div>

    <div class="computation-dates">
      <div class="dates-title">Dernier recalcul</div>
      <div v-if="datesLoading" class="dates-empty">Chargement…</div>
      <div v-else-if="datesError" class="dates-empty">
        Impossible de charger les dates de recalcul.
      </div>
      <ul v-else class="dates-list">
        <li
          v-for="row in computationRows"
          :key="row.period"
          class="dates-row"
          :class="{selected: row.period === selectedPeriod}"
        >
          <span>{{ row.label }}</span>
          <span>{{ formatComputedAt(row.computedAt) }}</span>
        </li>
      </ul>
    </div>

    <div class="mt-4 flex gap-2">
      <hit-button
        :label="running ? 'Calcul en cours…' : 'Relancer le calcul'"
        color="accent"
        prefix-icon="play_arrow"
        :disabled="running || !canRun"
        @click="run"
      />
      <hit-button
        :label="'Réinitialiser'"
        color="button"
        prefix-icon="reset_changes"
        :disabled="running"
        @click="resetForm"
      />
    </div>

    <div v-if="result" class="result mt-4">
      <div class="result-title" :class="{ok: result.ok, ko: !result.ok}">
        {{ result.ok ? 'Succès' : 'Erreur' }}
      </div>
      <pre class="result-pre">{{ resultText }}</pre>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

function buildPeriodOptions() {
  const options = [];
  const now = new Date();
  for (let offset = 0; offset < 12; offset += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    options.push({
      id: `${year}${month}`,
      label: `${year}-${month}`,
    });
  }
  return options;
}

export default defineComponent({
  name: 'ScriptComputeUsage',
  components: {HitButton, HitSelect},
  data() {
    const periodOptions = buildPeriodOptions();
    return {
      periodOptions,
      selectedPeriod: periodOptions[0]?.id || null,
      running: false,
      result: null,
      computedAtByPeriod: {},
      datesLoading: false,
      datesError: false,
    };
  },
  computed: {
    computationRows() {
      return this.periodOptions.map((option) => ({
        period: option.id,
        label: option.label,
        computedAt: this.computedAtByPeriod[option.id] || null,
      }));
    },
    canRun() {
      return !!this.selectedPeriod;
    },
    resultText() {
      try {
        return JSON.stringify(this.result, null, 2);
      } catch {
        return String(this.result);
      }
    },
  },
  mounted() {
    this.loadComputationDates();
  },
  methods: {
    formatComputedAt(value) {
      if (!value) return 'Aucun recalcul';
      try {
        return new Intl.DateTimeFormat('fr-BE', {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(new Date(value));
      } catch {
        return value;
      }
    },
    async loadComputationDates() {
      this.datesLoading = true;
      this.datesError = false;
      try {
        const rows = await adminApi.listUsageComputationDates(
          this.periodOptions.map((option) => option.id),
        );
        const byPeriod = {};
        (rows || []).forEach((row) => {
          byPeriod[row.period] = row.computed_at || null;
        });
        this.computedAtByPeriod = byPeriod;
      } catch {
        this.computedAtByPeriod = {};
        this.datesError = true;
      } finally {
        this.datesLoading = false;
      }
    },
    onPeriodChange(value) {
      this.selectedPeriod = value;
      this.result = null;
    },
    resetForm() {
      this.selectedPeriod = this.periodOptions[0]?.id || null;
      this.result = null;
    },
    async run() {
      if (!this.canRun) return;
      this.running = true;
      this.result = null;
      try {
        const res = await adminApi.computeUsage(this.selectedPeriod);
        this.result = {ok: true, ...(res || {})};
        await this.loadComputationDates();
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.running = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.82);
  padding: 0.9rem;
}

.panel-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
  margin-bottom: 0.15rem;
}

.panel-subtitle {
  color: rgba(100, 116, 139, 1);
  font-size: 0.9rem;
  line-height: 1.25rem;
  margin-bottom: 0.85rem;
}

.panel-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.computation-dates {
  margin-top: 0.85rem;
}

.dates-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.72);
  margin-bottom: 0.35rem;
}

.dates-empty {
  color: rgba(100, 116, 139, 1);
  font-size: 0.85rem;
}

.dates-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.2rem;
  max-width: 28rem;
}

.dates-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.28rem 0.45rem;
  border-radius: 0.4rem;
  font-size: 0.85rem;
  color: rgba(51, 65, 85, 1);
}

.dates-row.selected {
  background: rgba(3, 105, 90, 0.08);
  color: rgba(15, 23, 42, 0.95);
  font-weight: 600;
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.15rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem 0.65rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
